import React, {
	useLayoutEffect, useRef, useState, useEffect
} from 'react';
import classNames from 'classnames';
import { graphql } from 'gatsby';
import parse from 'html-react-parser';
import { Helmet } from 'react-helmet';

import RenderStaticHTML from '../components/RenderStaticHTML';
import PageMask from '../components/PageMask';
import RevealOnLoad from '../components/RevealOnLoad';

const ns = `page-job-detail`;

const JobDetail = ({ data, pageContext }) => {
	const applicationRef = useRef(null);
	const [isGreenhouseLoaded, setIsGreenhouseLoaded] = useState(false);
	const [iframeError, setIframeError] = useState(false);

	const {
		job: {
			title,
			location,
			content,
		},
	} = data;

	const {
		gh_Id: ghId, // set in /gatsby/createJobs.js
	} = pageContext;

	// Check if Greenhouse script is loaded, errors out after 3 seconds
	useEffect(() => {
		let retryCount = 0;
		const maxRetries = 30;

		const checkGreenhouse = () => {
			if (window.Grnhse) {
				setIsGreenhouseLoaded(true);
			} else if (retryCount < maxRetries) {
				retryCount += 1;

				setTimeout(checkGreenhouse, 100);
			} else {
				// eslint-disable-next-line no-console
				console.warn('Greenhouse script failed to load after 3 seconds');

				setIframeError(true);
			}
		};

		checkGreenhouse();
	}, []);

	// Check if iframe is loaded and set height to prevent layout shift
	const checkIframe = () => {
		const applicationEl = applicationRef.current;

		if (!applicationEl) return;

		const iframe = applicationEl.querySelector('iframe');

		if (!iframe) return;

		iframe.onload = () => {
			const iframeHeight = iframe.offsetHeight;

			if (iframeHeight > 0) {
				applicationEl.style.minHeight = `${ iframeHeight }px`;
			}
		};
	};

	useLayoutEffect(() => {
		if (!isGreenhouseLoaded || !ghId || !applicationRef.current) return;

		try {
			window.Grnhse.Iframe.load(ghId);

			checkIframe();

			setTimeout(checkIframe, 500);
			setTimeout(checkIframe, 1000);
			setTimeout(checkIframe, 2000);

			// Set error state if iframe doesn't load after 3 seconds
			setTimeout(() => {
				const applicationEl = applicationRef.current;

				if (applicationEl && !applicationEl.querySelector('iframe')) {
					setIframeError(true);
				}
			}, 3000);
		} catch (error) {
			// eslint-disable-next-line no-console
			console.error('Error loading Greenhouse iframe:', error);

			setIframeError(true);
		}
	}, [isGreenhouseLoaded, ghId]);

	const handleScrollTo = () => {
		const applicationEl = applicationRef.current;

		if (!applicationEl) return;

		const appElDistanceToTop = window.pageYOffset + applicationEl.getBoundingClientRect().top;

		window.scrollTo(0, appElDistanceToTop);
	};

	const rootClassnames = classNames({
		[`${ ns }`]: true,
	});

	return (
		<div className={rootClassnames}>
			<PageMask>
				<Helmet>
					<body data-header-theme={`${ 'dark' }`} />
				</Helmet>
				<div className={`container container--offset`}>
					<RevealOnLoad>
						<div className={`${ ns }__heading`}>
							{title && (
								<h1 className={`${ ns }__title h1-med`}>{title}</h1>
							)}
							<div className={`${ ns }__subtitle h3-reg`}>
								{location?.name && (
									<span>
										{location.name}
										&nbsp;–&nbsp;
									</span>
								)}
								<span>
									<button onClick={handleScrollTo}>Apply Now</button>
								</span>
							</div>
						</div>
						<div className={`${ ns }__body`}>
							<RenderStaticHTML className={`${ ns }__body`} html={parse(content)} />

							<div className={`${ ns }__application`}>
								{/* Loading message */}
								{!isGreenhouseLoaded && !iframeError && (
									<div className={`${ ns }__loading`}>
										<p>Loading application form...</p>
									</div>
								)}

								{/* Error message */}
								{iframeError && (
									<div className={`${ ns }__error`}>
										<p>
											Unable to load application form.
											Please try refreshing the page or contact us directly.
										</p>
									</div>
								)}

								{/* Application form */}
								<div ref={applicationRef} id={`grnhse_app`} />
							</div>
						</div>
					</RevealOnLoad>
				</div>
			</PageMask>
		</div>
	);
};

export default JobDetail;

export const jobDetailQuery = graphql`
	# these variables are passed in via createPage.pageContext in gatsby-node.js
	query GET_JOB(
		$gh_Id: Int
	) {
		# selecting the current job by gh_Id (Greenhouse Job ID)
		job: greenhouseJob(gh_Id: { eq: $gh_Id }) {
			title
			location {
				name
			}
			content
		}
	}
`;
